import apiClient from './apiClient';

// Driver Earnings Types (matching actual API)
export interface Driver {
  id: string;
  driverId: string;
  userId: string;

  // Personal info
  personalInfo?: {
    firstName: string;
    lastName: string;
  };
  contactInfo?: {
    phoneNumber: string;
    email: string;
  };

  // Backward compatibility (flat)
  name: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;

  status: string; // "active" in API
  applicationStatus: 'approved' | 'pending' | 'rejected';
  isActive: boolean;
  isAvailable: boolean;

  profilePictureUrl?: string;
  hasProfilePicture: boolean;
  rating: number;
  experienceYears?: number;
  primaryServiceType?: string;
  description?: string;

  licenseNumber?: string;
  serviceLocations?: string[];
  specializations?: string[];

  completionPercentage: number;
  createdAt: string;
  updatedAt: string;
  reviewedAt?: string;
}

export interface DriverPayout {
  id: string;
  driver: {
    id: string;
    code: string;
    name: string;
    email: string;
    phone: string;
  };
  amount: number;
  currency: string;
  status: 'pending' | 'approved' | 'completed' | 'failed' | 'cancelled';
  bankAccount: {
    accountName: string;
    accountNumber: string;
    bankCode: string;
    bankName: string;
  };
  requestedAt: string;
  processedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  failedReason: string | null;
  paystackTransferCode: string | null;
  adminNotes: string | null;
}

export interface DriversListResponse {
  success: boolean;
  data: {
    drivers: Driver[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface DriverPayoutsResponse {
  success: boolean;
  data: {
    payouts: DriverPayout[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
    stats?: {
      pending: {
        count: number;
        totalAmount: number;
      };
      completed: {
        count: number;
        totalAmount: number;
      };
      failed?: {
        count: number;
        totalAmount: number;
      };
    };
  };
}

export interface DriverEarningsFilters {
  page?: number;
  limit?: number;
  status?: 'approved' | 'pending' | 'rejected' | 'active' | 'all';
  applicationStatus?: 'approved' | 'pending' | 'rejected' | 'all';
}

export interface DriverPayoutFilters {
  page?: number;
  limit?: number;
  status?: 'pending' | 'approved' | 'completed' | 'failed' | 'cancelled';
  driverId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

// Detailed Driver Earnings Types (matching actual API)
export interface DriverDetailedEarnings {
  driver: {
    id: string;
    name: string;
    email: string;
    phone: string;
    status: string;
  };
  availableBalance: number;
  pendingEarnings: number;
  pendingWithdrawals: number;
  totalWithdrawn: number;
  totalEarnings: number;
  releaseInfo: {
    nextReleaseAt: string | null;
    nextReleaseAmount: number;
    hoursUntilRelease: number;
  };
  recentBookings: Array<{
    bookingNumber: string;
    amount: number;
    status: 'released' | 'locked';
    releaseDate: string;
  }>;
  currency: string;
}

export interface DriverDetailedEarningsFilters {
  period?: 'all' | 'today' | 'week' | 'month' | 'year';
  serviceType?: string;
  startDate?: string;
  endDate?: string;
}

class DriverEarningsService {
  /**
   * Get all drivers with their earnings summary
   */
  async getDrivers(filters?: DriverEarningsFilters): Promise<DriversListResponse> {
    try {
      const params = new URLSearchParams();

      if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.page) params.append('page', filters.page.toString());

      const response = await apiClient.get(
        `/api/v1/admin/drivers?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching drivers:', error);
      throw error;
    }
  }

  /**
   * Get driver payout requests with filtering
   */
  async getDriverPayouts(filters?: DriverPayoutFilters): Promise<DriverPayoutsResponse> {
    try {
      const params = new URLSearchParams();

      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.status) params.append('status', filters.status);
      if (filters?.driverId) params.append('driverId', filters.driverId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.search) params.append('search', filters.search);

      const response = await apiClient.get(
        `/api/v1/admin/payouts?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching driver payouts:', error);
      throw error;
    }
  }

  /**
   * Approve a pending payout
   */
  async approvePayout(payoutId: string, adminNotes?: string): Promise<void> {
    try {
      await apiClient.post(
        `/api/v1/admin/payouts/${payoutId}/approve`,
        { adminNotes }
      );
    } catch (error) {
      console.error('Error approving payout:', error);
      throw error;
    }
  }

  /**
   * Reject a pending payout
   */
  async rejectPayout(payoutId: string, reason: string): Promise<void> {
    try {
      await apiClient.post(
        `/api/v1/admin/payouts/${payoutId}/reject`,
        { reason }
      );
    } catch (error) {
      console.error('Error rejecting payout:', error);
      throw error;
    }
  }

  /**
   * Get detailed earnings breakdown for a specific driver
   */
  async getDriverDetailedEarnings(
    driverId: string,
    filters?: DriverDetailedEarningsFilters
  ): Promise<{ success: boolean; data: DriverDetailedEarnings; timestamp: string }> {
    try {
      const params = new URLSearchParams();

      if (filters?.period) params.append('period', filters.period);
      if (filters?.serviceType) params.append('serviceType', filters.serviceType);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await apiClient.get(
        `/api/v1/admin/drivers/${driverId}/earnings?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching driver detailed earnings:', error);
      throw error;
    }
  }

  /**
   * Calculate summary statistics from drivers data
   */
  async getEarningsSummary() {
    try {
      // Fetch all approved drivers
      const response = await this.getDrivers({ status: 'approved', limit: 1000 });
      const drivers = response.data.drivers;

      // Calculate totals
      // Note: API doesn't provide totalEarnings in list endpoint, need individual calls
      const totalEarnings = 0; // Would need to fetch individual earnings
      const totalDrivers = drivers.length;
      const activeDrivers = drivers.filter(d => d.isActive || d.status === 'active').length;
      const averageEarningsPerDriver = 0; // Would need individual earnings data

      // Get payout stats (optional - endpoint may not exist yet)
      let stats = {
        pending: { count: 0, totalAmount: 0 },
        completed: { count: 0, totalAmount: 0 },
      };

      try {
        const payoutsResponse = await this.getDriverPayouts({ limit: 1000 });
        if (payoutsResponse.data?.stats) {
          stats = payoutsResponse.data.stats;
        }
      } catch (error) {
        console.warn('Driver payouts endpoint not available, using zero stats:', error);
      }

      // Get top 10 drivers (by registration date since earnings not in list API)
      const topEarningDrivers = [...drivers]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 10)
        .map(driver => ({
          driverId: driver.id,
          driverName: driver.name || `${driver.firstName} ${driver.lastName}`,
          driverEmail: driver.email,
          driverNumber: driver.driverId || driver.id.substring(0, 8),
          totalEarnings: 0, // Not available in list API
          availableBalance: 0, // Not available in list API
          totalWithdrawn: 0, // Not available in list API
          completedTrips: 0, // Not available in list API
          rating: driver.rating || 0,
          status: driver.isActive ? 'active' as const : 'inactive' as const,
        }));

      return {
        totalEarnings,
        totalDrivers,
        activeDrivers,
        pendingPayouts: stats.pending.totalAmount,
        completedPayouts: stats.completed.totalAmount,
        averageEarningsPerDriver,
        topEarningDrivers,
        earningsTrend: [],
      };
    } catch (error) {
      console.error('Error calculating earnings summary:', error);
      throw error;
    }
  }

  /**
   * Export driver earnings to CSV
   */
  async exportDriverEarnings(filters?: DriverEarningsFilters): Promise<Blob> {
    try {
      const response = await this.getDrivers(filters);
      const drivers = response.data.drivers;

      // Create CSV content
      const headers = ['Driver ID', 'Driver Name', 'Email', 'Phone', 'Status', 'Application Status', 'Rating', 'Experience Years', 'Joined Date'];
      const rows = drivers.map(driver => [
        driver.driverId || driver.id,
        driver.name || `${driver.firstName} ${driver.lastName}`,
        driver.email,
        driver.phone,
        driver.status,
        driver.applicationStatus,
        (driver.rating || 0).toFixed(1),
        driver.experienceYears || 0,
        new Date(driver.createdAt).toLocaleDateString(),
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(',')),
      ].join('\n');

      return new Blob([csvContent], { type: 'text/csv' });
    } catch (error) {
      console.error('Error exporting driver earnings:', error);
      throw error;
    }
  }
}

export const driverEarningsService = new DriverEarningsService();
